import { normalizeAlphabetPrefix } from '@/utils/alphabet';
import { SupportedLanguage } from './language';
import type { BlogCategory } from './blog-category';

interface GenerateLocalizedUrlProps {
  path: string;
  language?: SupportedLanguage;
  baseUrl?: string;
  isProduction?: boolean;
}

type GenerateURLBaseProps = Omit<GenerateLocalizedUrlProps, 'path'>;

export const LINKS = {
  PRODUCTION_BASE_URL: 'https://3billion.io',
  BASE_URL: process.env.META_BASE_URL || 'https://3billion.io',
  GEBRA: '/gebra',
  SANGER: '/sanger-sequencing',
  WHOLE_EXOME: '/whole-exome-sequencing',
  WHOLE_GENOME: '/whole-genome-sequencing',
  INTERPRETER: '/genomic-data-analysis',
  GENE_BROWSER: '/gene-browser',
  GENE_BROWSER_PREFIX: '/gene-browser/prefix',
  GENE_BROWSER_GENE: '/gene-browser/gene',
  BLOG: '/blog',
  BLOG_CATEGORY: '/blog/category',
  BLOG_TAG: '/blog/tag',
  BLOG_SERIES: '/blog/series',
};

export const generateInterpreterUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: LINKS.INTERPRETER,
  });
};

export const generateWholeGenomeUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: LINKS.WHOLE_GENOME,
  });
};

export const generateWholeExomeUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: LINKS.WHOLE_EXOME,
  });
};

export const generateGebraUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: LINKS.GEBRA,
  });
};

export const generateSangerUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: LINKS.SANGER,
  });
};

export const generateHomeUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: '/',
  });
};

export const generateSupportUrl = (props: GenerateURLBaseProps) => {
  return generateLocalizedUrl({
    ...props,
    path: '/support',
  });
};

type GenerateBlogAuthorUrlProps = GenerateURLBaseProps & {
  authorId: string;
  page?: number;
};

export const generateBlogAuthorUrl = ({
  authorId,
  page = 1,
  language = 'en',
  isProduction,
}: GenerateBlogAuthorUrlProps) => {
  return generateLocalizedUrl({
    path: `/blog/author/${authorId}/${page}`,
    language,
    isProduction,
  });
};

type GenerateNewsAuthorUrlProps = GenerateURLBaseProps & {
  authorId: string;
  page?: number;
};

export const generateNewsAuthorUrl = ({
  authorId,
  page = 1,
  language = 'en',
}: GenerateNewsAuthorUrlProps) => {
  return generateLocalizedUrl({
    path: `/news/author/${authorId}/${page}`,
    language,
  });
};

type GenerateFAQItemUrlProps = GenerateURLBaseProps & {
  slug: string;
};

export const generateFAQItemUrl = ({
  slug,
  language = 'en',
}: GenerateFAQItemUrlProps) => {
  return generateLocalizedUrl({
    path: `/faq/item/${slug}`,
    language,
  });
};

type GenerateGeneBrowserItemUrlProps = GenerateURLBaseProps & {
  geneName: string;
};

export const generateGeneBrowserItemUrl = ({
  geneName,
  language = 'en',
}: GenerateGeneBrowserItemUrlProps) => {
  return generateLocalizedUrl({
    path: `${LINKS.GENE_BROWSER_GENE}/${geneName}`,
    language,
  });
};

/** 블로그 상세 상대 경로 — /blog/{slug} (locale prefix 는 Link/호출부가 처리) */
export const getBlogPostPath = (slug: string) => `${LINKS.BLOG}/${slug}`;

/** 블로그 카테고리 목록 상대 경로 — /blog/category/{category}/{page} */
export const getBlogCategoryPath = (
  category: string,
  page: number | string = 1,
) => `${LINKS.BLOG_CATEGORY}/${category}/${page}`;

/**
 * 블로그 검색 결과 상대 경로 — `/blog/search?q={검색어}[&category={카테고리}]`
 *
 * '전체'(`all`)는 검색 페이지의 기본값이라 URL 에 넣지 않는다 — 이 규칙을 검색 입력과
 * 페이지네이션이 각자 구현하면 갈리므로 여기서 소유한다. 페이지 번호는 호출부가
 * `&page=` 로 이어 붙인다.
 */
export const getBlogSearchPath = ({
  query,
  category,
}: {
  query: string;
  category?: BlogCategory;
}) => {
  const params = new URLSearchParams({ q: query });
  if (category && category !== 'all') params.set('category', category);

  return `${LINKS.BLOG}/search?${params}`;
};

/** 블로그 태그 페이지 상대 경로 — /blog/tag/{slug}/{page} (locale prefix 는 Link/호출부가 처리) */
export const getBlogTagPath = (slug: string, page: number | string = 1) =>
  `${LINKS.BLOG_TAG}/${slug}/${page}`;

/** 블로그 시리즈 페이지 상대 경로 — /blog/series/{slug}/{page} (locale prefix 는 Link/호출부가 처리) */
export const getBlogSeriesPath = (slug: string, page: number | string = 1) =>
  `${LINKS.BLOG_SERIES}/${slug}/${page}`;

export const getGeneBrowserPrefixPath = (prefix?: string): string => {
  if (!prefix) return LINKS.GENE_BROWSER;
  const normalizedPrefix = normalizeAlphabetPrefix(prefix);
  if (!normalizedPrefix) return LINKS.GENE_BROWSER;
  return `${LINKS.GENE_BROWSER_PREFIX}/${normalizedPrefix}`;
};

export const generateLocalizedUrl = ({
  baseUrl,
  path,
  language = 'en',
  isProduction = false,
}: GenerateLocalizedUrlProps): string => {
  const url =
    baseUrl || (isProduction ? LINKS.PRODUCTION_BASE_URL : LINKS.BASE_URL);

  return language === 'en' ? `${url}${path}` : `${url}/${language}${path}`;
};

export const generateImageUrl = (image: string): string => {
  return `${LINKS.PRODUCTION_BASE_URL}/images/${image}`;
};

/** 본문 첫 이미지가 없을 때 JSON-LD·OG 대표 이미지로 쓰는 사이트 기본 공유 이미지(절대 URL) */
export const DEFAULT_SHARE_IMAGE = generateImageUrl('og-image.png');
